import { useCallback, useEffect, useRef, useState } from "react";
import MicRecorder from "mic-recorder-to-mp3";

const DEFAULT_NOT_SUPPORTED = "Gravação de áudio não é suportada neste navegador.";
const DEFAULT_PERMISSION_DENIED =
  "Não foi possível acessar o microfone. Verifique as permissões do navegador.";

/**
 * Gravação MP3 no composer (atendimento e equipe) — mesma lógica e ciclo de vida.
 */
export function useComposerAudioRecording({
  onNotSupported,
  onPermissionDenied,
} = {}) {
  const [audioURL, setAudioURL] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingDuration, setRecordingDuration] = useState(0);

  const mp3RecorderRef = useRef(null);
  const recordingTimerRef = useRef(null);
  const discardRequestedRef = useRef(false);

  const clearRecordingTimer = useCallback(() => {
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
  }, []);

  useEffect(() => () => clearRecordingTimer(), [clearRecordingTimer]);

  const revokeAudioUrl = useCallback((url) => {
    if (url && typeof url === "string" && url.startsWith("blob:")) {
      URL.revokeObjectURL(url);
    }
  }, []);

  const handleRemoveAudio = useCallback(() => {
    setAudioURL((prev) => {
      revokeAudioUrl(prev);
      return null;
    });
  }, [revokeAudioUrl]);

  const clearAudio = useCallback(() => {
    setAudioURL((prev) => {
      revokeAudioUrl(prev);
      return null;
    });
  }, [revokeAudioUrl]);

  const startRecording = useCallback(async () => {
    if (!navigator.mediaDevices) {
      (onNotSupported || (() => alert(DEFAULT_NOT_SUPPORTED)))();
      return;
    }
    try {
      if (!mp3RecorderRef.current) {
        mp3RecorderRef.current = new MicRecorder({ bitRate: 128 });
      }
      await mp3RecorderRef.current.start();
      setIsRecording(true);
      setRecordingDuration(0);
      clearRecordingTimer();
      recordingTimerRef.current = setInterval(() => {
        setRecordingDuration((d) => d + 1);
      }, 1000);
    } catch (e) {
      console.error("[useComposerAudioRecording] start:", e);
      (onPermissionDenied || (() => alert(DEFAULT_PERMISSION_DENIED)))();
    }
  }, [clearRecordingTimer, onNotSupported, onPermissionDenied]);

  const stopRecording = useCallback(() => {
    discardRequestedRef.current = false;
    clearRecordingTimer();
    setRecordingDuration(0);
    if (!mp3RecorderRef.current) {
      setIsRecording(false);
      return;
    }
    mp3RecorderRef.current
      .stop()
      .getMp3()
      .then(([, blob]) => {
        if (discardRequestedRef.current) {
          discardRequestedRef.current = false;
          setIsRecording(false);
          return;
        }
        setAudioURL(URL.createObjectURL(blob));
        setIsRecording(false);
      })
      .catch((e) => {
        console.error("[useComposerAudioRecording] stop:", e);
        discardRequestedRef.current = false;
        setIsRecording(false);
      });
  }, [clearRecordingTimer]);

  const discardRecording = useCallback(() => {
    discardRequestedRef.current = true;
    clearRecordingTimer();
    setRecordingDuration(0);
    if (!mp3RecorderRef.current) {
      setIsRecording(false);
      return;
    }
    mp3RecorderRef.current
      .stop()
      .getMp3()
      .then(() => {
        discardRequestedRef.current = false;
        setIsRecording(false);
      })
      .catch(() => {
        discardRequestedRef.current = false;
        setIsRecording(false);
      });
  }, [clearRecordingTimer]);

  return {
    audioURL,
    isRecording,
    recordingDuration,
    startRecording,
    stopRecording,
    discardRecording,
    handleRemoveAudio,
    clearAudio,
  };
}
